import { useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import FormTableContacts from '@components/my-prescriptions/FormTableContacts';
import { IContactLensDetails } from '@interfaces/myAppointmentInterfaces';
import { calculateNumberBox, getLabelFallback } from '@utils/utils';

import PlaceholderImageCL from '@images/svg-icons/placeholderImageCL.svg'
import InfoIcon from '@images/svg-icons/infoIcon.svg'

// For validation add this in props
type PrescriptionContactFormBoxProps = {
    isNotEditable?: boolean;
    doubleProduct?: boolean;
    prescriptionValuesError?: boolean;
    rightCL?: IContactLensDetails;
    leftCL?: IContactLensDetails;
    powerOD?;
    setPowerOD?;
    powerOS?;
    setPowerOS?;
    bcOD?;
    setBcOD?;
    bcOS?;
    setBcOS?;
    diaOD?;
    setDiaOD?;
    diaOS?;
    setDiaOS?;
    cylOD?;
    setCylOD?;
    cylOS?;
    setCylOS?;
    axisOD?;
    setAxisOD?;
    axisOS?;
    setAxisOS?;
    dominanceOD?;
    setDominanceOD?;
    dominanceOS?;
    setDominanceOS?;
    boxOD?;
    setBoxOD?;
    boxOS?;
    setBoxOS?;
    colorOD?;
    setColorOD?;
    colorOS?;
    setColorOS?;
    addOD?;
    setAddOD?;
    addOS?;
    setAddOS?;
    formErrors?;
    setFormErrors?;
    prescriptionErrorAll?;
    setPrescriptionErrorAll?;
    annualSupplyDiscount?;
    annualSupplyQty?;
    sixMonthDiscount?;
    sixMonthQty?;
    errorOnCreate?;
    isBuyableRight?;
    isBuyableLeft?;
    isPrescriptionRetail?;
};

const PrescriptionContactFormBox = (props: PrescriptionContactFormBoxProps): JSX.Element => {
    const selectPlaceholder = getLabelFallback('ADD_PRESCRIPTION_STEP_1_ADD_PLACEHOLDER');

    const formValues = {
        prescription: {
            right_eye_power: {
                value: props.powerOD,
                setter: props.setPowerOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_power'] : false,
            },
            left_eye_power: {
                value: props.powerOS,
                setter: props.setPowerOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_power'] : false,
            },
            right_eye_bc: {
                value: props.bcOD,
                setter: props.setBcOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_bc'] : false,
            },
            left_eye_bc: {
                value: props.bcOS,
                setter: props.setBcOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_bc'] : false,
            },
            right_eye_dia: {
                value: props.diaOD,
                setter: props.setDiaOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_dia'] : false,
            },
            left_eye_dia: {
                value: props.diaOS,
                setter: props.setDiaOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_dia'] : false,
            },
            right_eye_cyl: {
                value: props.cylOD,
                setter: props.setCylOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_cyl'] : false,
            },
            left_eye_cyl: {
                value: props.cylOS,
                setter: props.setCylOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_cyl'] : false,
            },
            right_eye_axis: {
                value: props.axisOD,
                setter: props.setAxisOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_axis'] : false,
            },
            left_eye_axis: {
                value: props.axisOS,
                setter: props.setAxisOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_axis'] : false,
            },
            right_eye_color: {
                value: props.colorOD,
                setter: props.setColorOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_color'] : false,
            },
            left_eye_color: {
                value: props.colorOS,
                setter: props.setColorOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_color'] : false,
            },
            right_eye_add: {
                value: props.addOD,
                setter: props.setAddOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_add'] : false,
            },
            left_eye_add: {
                value: props.addOS,
                setter: props.setAddOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_add'] : false,
            },
            right_eye_dominance: {
                value: props.dominanceOD,
                setter: props.setDominanceOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_dominance'] : false,
            },
            left_eye_dominance: {
                value: props.dominanceOS,
                setter: props.setDominanceOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_dominance'] : false,
            },
            right_eye_boxes: {
                value: props.boxOD,
                setter: props.setBoxOD,
                hasError: !!props.formErrors ? props.formErrors['right_eye_boxes'] : false,
            },
            left_eye_boxes: {
                value: props.boxOS,
                setter: props.setBoxOS,
                hasError: !!props.formErrors ? props.formErrors['left_eye_boxes'] : false,
            },
        },
    };

    /**
     * If rightCL or leftCL are SKUs (only one value per attribute),
     * set the selected prescription values in the form
     */
    useEffect(() => {        
        if (!!props.rightCL) {
            if (props.rightCL?.attributes?.power?.length === 1) {
                props.setPowerOD(parseFloat(props.rightCL?.attributes?.power[0]));
                props.setPowerOS(parseFloat(props.rightCL?.attributes?.power[0]));
            }
            if (props.rightCL?.attributes?.baseCurve?.length === 1) {
                props.setBcOD(parseFloat(props.rightCL?.attributes?.baseCurve[0]));
                props.setBcOS(parseFloat(props.rightCL?.attributes?.baseCurve[0]));
            }
            if (props.rightCL?.attributes?.diameter?.length === 1) {
                props.setDiaOD(parseFloat(props.rightCL?.attributes?.diameter[0]));
                props.setDiaOS(parseFloat(props.rightCL?.attributes?.diameter[0]));
            }
            if (props.rightCL?.attributes?.cylinder?.length === 1) {
                props.setCylOD(parseFloat(props.rightCL?.attributes?.cylinder[0]));
                props.setCylOS(parseFloat(props.rightCL?.attributes?.cylinder[0]));
            }
            if (props.rightCL?.attributes?.axis?.length === 1) {
                props.setAxisOD(parseFloat(props.rightCL?.attributes?.axis[0]));
                props.setAxisOS(parseFloat(props.rightCL?.attributes?.axis[0]));
            }
            if (props.rightCL?.attributes?.color?.length === 1) {
                props.setColorOD(props.rightCL?.attributes?.color[0]);
                props.setColorOS(props.rightCL?.attributes?.color[0]);
            }
            if (props.rightCL?.attributes?.add?.length === 1) {
                props.setAddOD(props.rightCL?.attributes?.add[0]);
                props.setAddOS(props.rightCL?.attributes?.add[0]);
            }
            if (props.rightCL?.attributes?.dominance?.length === 1) {
                props.setDominanceOD(props.rightCL?.attributes?.dominance[0] ==='True' ? 'D' : 'N');
                props.setDominanceOS(props.rightCL?.attributes?.dominance[0] ==='True' ? 'D' : 'N');
            }
        }
        if (!!props.leftCL) {
            if (props.leftCL?.attributes?.power?.length === 1) {
                props.setPowerOS(parseFloat(props.leftCL?.attributes?.power[0]));
            }
            if (props.leftCL?.attributes?.baseCurve?.length === 1) {
                props.setBcOS(parseFloat(props.leftCL?.attributes?.baseCurve[0]));
            }
            if (props.leftCL?.attributes?.diameter?.length === 1) {
                props.setDiaOS(parseFloat(props.leftCL?.attributes?.diameter[0]));
            }
            if (props.leftCL?.attributes?.cylinder?.length === 1) {
                props.setCylOS(parseFloat(props.leftCL?.attributes?.cylinder[0]));
            }
            if (props.leftCL?.attributes?.axis?.length === 1) {
                props.setAxisOS(parseFloat(props.leftCL?.attributes?.axis[0]));
            }
            if (props.leftCL?.attributes?.color?.length === 1) {
                props.setColorOS(props.leftCL?.attributes?.color[0]);
            }
            if (props.leftCL?.attributes?.add?.length === 1) {
                props.setAddOS(props.leftCL?.attributes?.add[0]);
            }
            if (props.leftCL?.attributes?.dominance?.length === 1) {
                props.setDominanceOS(props.leftCL?.attributes?.dominance[0] ==='True' ? 'D' : 'N');
            }
        }
    }, [props.rightCL, props.leftCL]);

    // CREATE FOR RIGHT AND LEFT I CREATE THIS FOR RIGHT
    const prescriptionTableColumns = [
        {
            id: 'power',
            type: 'power',
            placeholder: 'Power',
            required: true,
            name: 'Power',
            values: props.rightCL?.attributes?.power,
            enabled:
                !!props.rightCL?.attributes?.power &&
                ((props.rightCL?.attributes?.power[0] != '' &&
                    props.rightCL?.attributes?.power[0] != null) ||
                    (props.rightCL?.attributes?.power[1] != '' &&
                        props.rightCL?.attributes?.power[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.power,
            rows: [
                {
                    id: 'right_eye_power',
                },
                {
                    id: 'left_eye_power',
                },
            ],
        },
        {
            id: 'bc',
            type: 'bc',
            placeholder: 'BC',
            required: true,
            name: 'BC',
            values: props.rightCL?.attributes?.baseCurve,
            enabled:
                !!props.rightCL?.attributes?.baseCurve &&
                ((props.rightCL?.attributes?.baseCurve[0] != '' &&
                    props.rightCL?.attributes?.baseCurve[0] != null) ||
                    (props.rightCL?.attributes?.baseCurve[1] != '' &&
                        props.rightCL?.attributes?.baseCurve[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.baseCurve,
            rows: [
                {
                    id: 'right_eye_bc',
                },
                {
                    id: 'left_eye_bc',
                },
            ],
        },
        {
            id: 'dia',
            type: 'dia',
            placeholder: 'DIA',
            required: true,
            name: 'DIA',
            values: props.rightCL?.attributes?.diameter,
            enabled:
                !!props.rightCL?.attributes?.diameter &&
                ((props.rightCL?.attributes?.diameter[0] != '' &&
                    props.rightCL?.attributes?.diameter[0] != null) ||
                    (props.rightCL?.attributes?.diameter[1] != '' &&
                        props.rightCL?.attributes?.diameter[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.diameter,
            rows: [
                {
                    id: 'right_eye_dia',
                },
                {
                    id: 'left_eye_dia',
                },
            ],
        },
        {
            id: 'cyl',
            type: 'cyl',
            placeholder: 'Cylinder CYL',
            required: true,
            name: 'CYL',
            values: props.rightCL?.attributes?.cylinder,
            enabled:
                !!props.rightCL?.attributes?.cylinder &&
                ((props.rightCL?.attributes?.cylinder[0] != '' &&
                    props.rightCL?.attributes?.cylinder[0] != null) ||
                    (props.rightCL?.attributes?.cylinder[1] != '' &&
                        props.rightCL?.attributes?.cylinder[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.cylinder,
            rows: [
                {
                    id: 'right_eye_cyl',
                },
                {
                    id: 'left_eye_cyl',
                },
            ],
        },
        {
            id: 'axis',
            type: 'axis',
            placeholder: '',
            required: true,
            name: 'Axis',
            values: props.rightCL?.attributes?.axis,
            enabled:
                !!props.rightCL?.attributes?.axis &&
                ((props.rightCL?.attributes?.axis[0] != '' &&
                    props.rightCL?.attributes?.axis[0] != null) ||
                    (props.rightCL?.attributes?.axis[1] != '' &&
                        props.rightCL?.attributes?.axis[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.axis,
            rows: [
                {
                    id: 'right_eye_axis',
                },
                {
                    id: 'left_eye_axis',
                },
            ],
        },
        {
            id: 'color',
            type: 'color',
            placeholder: '',
            required: true,
            name: 'Color',
            values: props.rightCL?.attributes?.color,
            enabled:
                !!props.rightCL?.attributes?.color &&
                ((props.rightCL?.attributes?.color[0] != '' &&
                    props.rightCL?.attributes?.color[0] != null) ||
                    (props.rightCL?.attributes?.color[1] != '' &&
                        props.rightCL?.attributes?.color[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.color,
            rows: [
                {
                    id: 'right_eye_color',
                },
                {
                    id: 'left_eye_color',
                },
            ],
        },
        {
            id: 'add',
            type: 'add',
            placeholder: '',
            required: true,
            name: 'Add',
            values: props.rightCL?.attributes?.add,
            enabled:
                !!props.rightCL?.attributes?.add &&
                ((props.rightCL?.attributes?.add[0] != '' &&
                    props.rightCL?.attributes?.add[0] != null) ||
                    (props.rightCL?.attributes?.add[1] != '' &&
                        props.rightCL?.attributes?.add[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.add,
            rows: [
                {
                    id: 'right_eye_add',
                },
                {
                    id: 'left_eye_add',
                },
            ],
        },
        {
            id: 'dom',
            type: 'dom',
            placeholder: '',
            required: true,
            name: 'dom',
            values: props.rightCL?.attributes?.dominance?.map((el)=>el==='True'?'D':'N'),
            enabled:
                !!props.rightCL?.attributes?.dominance &&
                ((props.rightCL?.attributes?.dominance[0] != '' &&
                    props.rightCL?.attributes?.dominance[0] != null) ||
                    (props.rightCL?.attributes?.dominance[1] != '' &&
                        props.rightCL?.attributes?.dominance[1] != null)),
            otherIsEnabled: !!props.leftCL?.attributes?.dominance,
            rows: [
                {
                    id: 'right_eye_dominance',
                },
                {
                    id: 'left_eye_dominance',
                },
            ],
        },
    ];

    const prescriptionTableColumnsLeft = [
        {
            id: 'power',
            type: 'power',
            placeholder: 'Power',
            required: true,
            name: 'Power',
            values: props.leftCL?.attributes?.power,
            enabled: !!props.leftCL?.attributes?.power,
            otherIsEnabled: !!props.rightCL?.attributes?.power,
            rows: [
                {
                    id: 'left_eye_power',
                },
            ],
        },
        {
            id: 'bc',
            type: 'bc',
            placeholder: 'BC',
            required: true,
            name: 'BC',
            values: props.leftCL?.attributes?.baseCurve,
            enabled: !!props.leftCL?.attributes?.baseCurve,
            otherIsEnabled: !!props.rightCL?.attributes?.baseCurve,
            rows: [
                {
                    id: 'left_eye_bc',
                },
            ],
        },
        {
            id: 'dia',
            type: 'dia',
            placeholder: 'DIA',
            required: true,
            name: 'DIA',
            values: props.leftCL?.attributes?.diameter,
            enabled: !!props.leftCL?.attributes?.diameter,
            otherIsEnabled: !!props.rightCL?.attributes?.diameter,
            rows: [
                {
                    id: 'left_eye_dia',
                },
            ],
        },
        {
            id: 'cyl',
            type: 'cyl',
            placeholder: 'Cylinder CYL',
            required: true,
            name: 'CYL',
            values: props.leftCL?.attributes?.cylinder,
            enabled: !!props.leftCL?.attributes?.cylinder,
            otherIsEnabled: !!props.rightCL?.attributes?.cylinder,
            rows: [
                {
                    id: 'left_eye_cyl',
                },
            ],
        },
        {
            id: 'axis',
            type: 'axis',
            placeholder: '',
            required: true,
            name: 'Axis',
            values: props.leftCL?.attributes?.axis,
            enabled: !!props.leftCL?.attributes?.axis,
            otherIsEnabled: !!props.rightCL?.attributes?.axis,
            rows: [
                {
                    id: 'left_eye_axis',
                },
            ],
        },
        {
            id: 'color',
            type: 'color',
            placeholder: '',
            required: true,
            name: 'Color',
            values: props.leftCL?.attributes?.color,
            enabled: !!props.leftCL?.attributes?.color,
            otherIsEnabled: !!props.rightCL?.attributes?.color,
            rows: [
                {
                    id: 'left_eye_color',
                },
            ],
        },
        {
            id: 'add',
            type: 'add',
            placeholder: '',
            required: true,
            name: 'Add',
            values: props.leftCL?.attributes?.add,
            enabled: !!props.leftCL?.attributes?.add,
            otherIsEnabled: !!props.rightCL?.attributes?.add,
            rows: [
                {
                    id: 'left_eye_add',
                },
            ],
        },
    ];

    const calculatePrice = () => {
        let result = 1;
        let numberBox = calculateNumberBox(props.boxOD, props.boxOS);

        if (numberBox >= props.annualSupplyQty) {
            //annual
            result = numberBox * props.rightCL?.price - props.annualSupplyDiscount;
        } else if (numberBox >= props.sixMonthQty) {
            //six month
            result = numberBox * props.rightCL?.price - props.sixMonthDiscount;
        } else {
            //No Discount
            result = numberBox * props.rightCL?.price;
        }
        return result.toFixed(2);
    };

    return (
        <>
            {!!props.rightCL && (!props.isPrescriptionRetail || (!props.isBuyableLeft && props.isBuyableRight) || (!props.isBuyableLeft && !props.isBuyableRight) || (props.isBuyableLeft && props.isBuyableRight)) && (
                <div className="first-part-form cl-form">
                    {!!props.rightCL?.image && (
                        <LazyLoadImage
                            className="cl-form-image"
                            src={
                                props.rightCL
                                    ?.image
                            }
                            alt=""
                        />
                    )}

                    {!(!!props.rightCL?.image) && (
                        <div className="first-part-form-image">
                            <div className="first-part-form-image-img">
                                <PlaceholderImageCL />
                            </div>
                            <div className="first-part-form-image-text">
                                <span className="first-part-form-image-text-bold">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_1')}
                                </span>
                                <span className="first-part-form-image-text">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_2')}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="cl-row">
                        <div className="cl-form-details">
                            <div className="cl-form-details__product">{props.rightCL?.name}</div>
                            <div className="cl-form-details__brand">{props.rightCL?.brand}</div>
                        </div>

                        {/*
                            IF double product is right
                        */}
                        <FormTableContacts
                            leftEye={!props.doubleProduct} // props.doubleProduct è true se ci stanno due prodotti quindi leftEye false e quindi prodotto a sinistra stampato giù, false se c'è ne sta solo uno e quindi leftEye è true quindi stampa nella stessa casella
                            rightEye={true}
                            columns={prescriptionTableColumns}
                            selector={'prescription'}
                            cylOD={props.cylOD}
                            cylOS={props.cylOS}
                            selectPlaceholder={selectPlaceholder}
                            formValues={formValues}
                            formErrors={props.formErrors}
                            isNotEditable={props.isNotEditable}
                            isBuyableClLeft={props.isBuyableLeft}
                            isBuyableClRight={props.isBuyableRight}
                            isPrescriptionRetail={props.isPrescriptionRetail}
                        />

                        {props.isNotEditable && !!props.rightCL &&
                            ((!!props.boxOD && props.boxOD !== '' && props.boxOD !== '0') ||
                                (!props.doubleProduct && !!props.boxOS && props.boxOS !== '' && props.boxOS !== '0')) && !!props.rightCL?.price &&
                            (!props.doubleProduct ? (
                                <div className="cl-subtotal">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                        .replace(
                                            '##NUMBOX##',
                                            calculateNumberBox(props.boxOD, props.boxOS).toString()
                                        )
                                        .replace('##NUMVALUE##', calculatePrice().toString())}
                                </div>
                            ) : (
                                <div className="cl-subtotal">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                        .replace('##NUMBOX##', props.boxOD.toString())
                                        .replace(
                                            '##NUMVALUE##',
                                            (props.rightCL?.price * props.boxOD).toFixed(2)
                                        )}
                                </div>
                            ))}

                        {!!props.errorOnCreate &&
                            (props.errorOnCreate === 'SKU_RIGHT' ||
                                (props.errorOnCreate === 'SKU_LEFT' && !props.doubleProduct)) && (
                                <div className="error-label">
                                    {getLabelFallback(
                                        'ERROR_SUBMIT_PRESCRIPTION_' + props.errorOnCreate
                                    )}
                                </div>
                            )}

                        {props.prescriptionErrorAll && !props.doubleProduct && (
                            <div className="prescription-values-error-div">
                                <span className="prescription-values-error">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_VALUES_ERROR')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {props.isPrescriptionRetail && (props.isBuyableLeft && !props.isBuyableRight) && (
                <>
                    <div className="not-buyable">
                        <div className="not-buyable-cl">
                            <div className="not-buyable-cl-text">
                                <InfoIcon />
                                <span>This product is not available for reorder</span>
                            </div>
                        </div>
                        <div className="not-buyable-cl-container">
                            <div className='not-buyable-cl-container-overlay'></div>
                            <div className="first-part-form cl-form">
                                {!!props.rightCL?.image && (
                                    <LazyLoadImage
                                        className="cl-form-image"
                                        src={
                                            props.rightCL
                                                ?.image
                                        }
                                        alt=""
                                    />
                                )}

                                {!(!!props.rightCL?.image) && (
                                    <div className="first-part-form-image">
                                        <div className="first-part-form-image-img">
                                            <PlaceholderImageCL />
                                        </div>
                                        <div className="first-part-form-image-text">
                                            <span className="first-part-form-image-text-bold">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_1')}
                                            </span>
                                            <span className="first-part-form-image-text">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_2')}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className="cl-row">
                                    <div className="cl-form-details">
                                        <div className="cl-form-details__product">{props.rightCL?.name}</div>
                                        <div className="cl-form-details__brand">{props.rightCL?.brand}</div>
                                    </div>

                                    {/*
                                        IF double product is right
                                    */}
                                    <FormTableContacts
                                        leftEye={!props.doubleProduct} // props.doubleProduct è true se ci stanno due prodotti quindi leftEye false e quindi prodotto a sinistra stampato giù, false se c'è ne sta solo uno e quindi leftEye è true quindi stampa nella stessa casella
                                        rightEye={true}
                                        columns={prescriptionTableColumns}
                                        selector={'prescription'}
                                        cylOD={props.cylOD}
                                        cylOS={props.cylOS}
                                        selectPlaceholder={selectPlaceholder}
                                        formValues={formValues}
                                        formErrors={props.formErrors}
                                        isNotEditable={props.isNotEditable}
                                        isBuyableClLeft={props.isBuyableLeft}
                                        isBuyableClRight={props.isBuyableRight}
                                        isPrescriptionRetail={props.isPrescriptionRetail}
                                    />

                                    {props.isNotEditable && !!props.rightCL &&
                                        ((!!props.boxOD && props.boxOD !== '' && props.boxOD !== '0' && props.isBuyableRight) ||
                                            (!props.doubleProduct && !!props.boxOS && props.boxOS !== '' && props.boxOS !== '0' && props.isBuyableLeft)) && !!props.rightCL?.price &&
                                        (!props.doubleProduct ? (
                                            <div className="cl-subtotal">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                                    .replace(
                                                        '##NUMBOX##',
                                                        calculateNumberBox(props.boxOD, props.boxOS).toString()
                                                    )
                                                    .replace('##NUMVALUE##', calculatePrice().toString())}
                                            </div>
                                        ) : (
                                            <div className="cl-subtotal">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                                    .replace('##NUMBOX##', props.boxOD.toString())
                                                    .replace(
                                                        '##NUMVALUE##',
                                                        (props.rightCL?.price * props.boxOD).toFixed(2)
                                                    )}
                                            </div>
                                        ))}

                                    {!!props.errorOnCreate &&
                                        (props.errorOnCreate === 'SKU_RIGHT' ||
                                            (props.errorOnCreate === 'SKU_LEFT' && !props.doubleProduct)) && (
                                            <div className="error-label">
                                                {getLabelFallback(
                                                    'ERROR_SUBMIT_PRESCRIPTION_' + props.errorOnCreate
                                                )}
                                            </div>
                                        )}

                                    {props.prescriptionErrorAll && !props.doubleProduct && (
                                        <div className="prescription-values-error-div">
                                            <span className="prescription-values-error">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_VALUES_ERROR')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {!!props.doubleProduct && !!props.leftCL && (!props.isPrescriptionRetail || (!props.isBuyableLeft && !props.isBuyableRight) || props.isBuyableLeft) && (
                <div className="first-part-form cl-form">
                    {!!props.leftCL?.image && (
                        <LazyLoadImage
                            className="cl-form-image"
                            src={
                                props.leftCL
                                    ?.image
                            }
                            alt=""
                        />
                    )}

                    {!(!!props.leftCL?.image) && (
                        <div className="first-part-form-image">
                            <div className="first-part-form-image-img">
                                <PlaceholderImageCL />
                            </div>
                            <div className="first-part-form-image-text">
                                <span className="first-part-form-image-text-bold">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_1')}
                                </span>
                                <span className="first-part-form-image-text">
                                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_2')}
                                </span>
                            </div>
                        </div>
                    )}


                    <div className="cl-row">
                        <div className="cl-form-details">
                            <div className="cl-form-details__product">{props.leftCL?.name}</div>
                            <div className="cl-form-details__brand">{props.leftCL?.brand}</div>
                        </div>

                        {/*
                            IF double product is left
                        */}
                        <FormTableContacts
                            leftEye={true}
                            rightEye={!props.doubleProduct}
                            columns={prescriptionTableColumnsLeft}
                            selector={'prescription'}
                            cylOD={props.cylOD}
                            cylOS={props.cylOS}
                            selectPlaceholder={selectPlaceholder}
                            formValues={formValues}
                            formErrors={props.formErrors}
                            isNotEditable={props.isNotEditable}
                            isBuyableClLeft={props.isBuyableLeft}
                            isBuyableClRight={props.isBuyableRight}
                            isPrescriptionRetail={props.isPrescriptionRetail}
                        />

                        {!!props.errorOnCreate && props.errorOnCreate === 'SKU_LEFT' && (
                            <div className="error-label">
                                {getLabelFallback(
                                    'ERROR_SUBMIT_PRESCRIPTION_' + props.errorOnCreate
                                )}
                            </div>
                        )}

                        {props.isNotEditable && !!props.boxOS && props.boxOS !== '' && !!props.leftCL && !!props.leftCL?.price && (
                            <div className="cl-subtotal">
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                    .replace('##NUMBOX##', props.boxOS.toString())
                                    .replace(
                                        '##NUMVALUE##',
                                        (props.leftCL.price * props.boxOS).toFixed(2)
                                    )}
                            </div>
                        )}

                        {props.prescriptionErrorAll && (
                            <span className="prescription-values-error">
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_VALUES_ERROR')}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {props.doubleProduct && props.isPrescriptionRetail && !props.isBuyableLeft && props.isBuyableRight && (
                <>
                    <div className="not-buyable">
                        <div className="not-buyable-cl">
                            <div className="not-buyable-cl-text">
                                <InfoIcon />
                                <span>This product is not available for reorder</span>
                            </div>
                        </div>
                        <div className="not-buyable-cl-container">
                            <div className='not-buyable-cl-container-overlay'></div>
                            <div className={`first-part-form cl-form ${!props.isBuyableLeft ? 'not-buyable-cl-form' : ''}`}>
                                {!!props.leftCL?.image && (
                                    <LazyLoadImage
                                        className="cl-form-image"
                                        src={
                                            props.leftCL
                                                ?.image
                                        }
                                        alt=""
                                    />
                                )}

                                {!(!!props.leftCL?.image) && (
                                    <div className="first-part-form-image">
                                        <div className="first-part-form-image-img">
                                            <PlaceholderImageCL />
                                        </div>
                                        <div className="first-part-form-image-text">
                                            <span className="first-part-form-image-text-bold">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_1')}
                                            </span>
                                            <span className="first-part-form-image-text">
                                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_PLACEHOLDER_TEXT_2')}
                                            </span>
                                        </div>
                                    </div>
                                )}


                                <div className="cl-row">
                                    <div className="cl-form-details">
                                        <div className="cl-form-details__product">{props.leftCL?.name}</div>
                                        <div className="cl-form-details__brand">{props.leftCL?.brand}</div>
                                    </div>

                                    {/*
                                        IF double product is left
                                    */}
                                    <FormTableContacts
                                        leftEye={true}
                                        rightEye={!props.doubleProduct}
                                        columns={prescriptionTableColumnsLeft}
                                        selector={'prescription'}
                                        cylOD={props.cylOD}
                                        cylOS={props.cylOS}
                                        selectPlaceholder={selectPlaceholder}
                                        formValues={formValues}
                                        formErrors={props.formErrors}
                                        isNotEditable={props.isNotEditable}
                                        isBuyableClLeft={props.isBuyableLeft}
                                        isBuyableClRight={props.isBuyableRight}
                                        isPrescriptionRetail={props.isPrescriptionRetail}
                                    />

                                    {!!props.errorOnCreate && props.errorOnCreate === 'SKU_LEFT' && (
                                        <div className="error-label">
                                            {getLabelFallback(
                                                'ERROR_SUBMIT_PRESCRIPTION_' + props.errorOnCreate
                                            )}
                                        </div>
                                    )}

                                    {props.isNotEditable && !!props.boxOS && props.boxOS !== '' && !!props.leftCL && !!props.leftCL?.price && !!props.isBuyableLeft && (
                                        <div className="cl-subtotal">
                                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SUBTOTAL')
                                                .replace('##NUMBOX##', props.boxOS.toString())
                                                .replace(
                                                    '##NUMVALUE##',
                                                    (props.leftCL.price * props.boxOS).toFixed(2)
                                                )}
                                        </div>
                                    )}

                                    {props.prescriptionErrorAll && (
                                        <span className="prescription-values-error">
                                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_VALUES_ERROR')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {props.prescriptionValuesError && (
                <div className="prescription-values-form-error">
                    {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_VALUES_ERROR')}
                </div>
            )}
        </>
    );
};

export default PrescriptionContactFormBox;
